import { createTheme } from '@mui/material/styles';
import type { Theme } from '@mui/material/styles';
import type { AcceptanceStatus } from './types';

/** Xanh dương gốc của WEBQN — dùng khi cổng truy cập không khai báo màu riêng. */
export const DEFAULT_BRAND_COLOR = '#1565c0';

/** Chỉ nhận đúng `#rrggbb`; mọi thứ khác bị loại để không chèn được CSS lạ vào palette. */
const BRAND_COLOR_RE = /^#[0-9a-f]{6}$/;

/** Palette primary gốc — giữ nguyên light/dark đã tinh chỉnh tay theo mockup. */
const DEFAULT_PRIMARY = {
  main: DEFAULT_BRAND_COLOR,
  light: '#5e92f3',
  dark: '#003c8f',
  contrastText: '#ffffff',
};

/**
 * Suy ra palette primary từ một mã màu thương hiệu duy nhất.
 * `augmentColor` của MUI lo phần light/dark và tự chọn màu chữ tương phản,
 * nên admin chỉ phải chọn 1 ô màu mà vẫn đọc được chữ trên nền.
 * Màu hỏng hoặc rỗng → lùi về palette gốc thay vì làm vỡ giao diện.
 */
function resolvePrimary(brandColor?: string | null) {
  const normalized = typeof brandColor === 'string' ? brandColor.trim().toLowerCase() : '';
  if (!BRAND_COLOR_RE.test(normalized) || normalized === DEFAULT_BRAND_COLOR) return DEFAULT_PRIMARY;
  return createTheme().palette.augmentColor({ color: { main: normalized } });
}

/**
 * Theme WEBQN — theo mockup:
 *  - primary: xanh dương, hoặc màu thương hiệu của cổng truy cập hiện tại
 *  - nền: xám nhạt #f5f6f8
 *  - font: Roboto (nhúng qua @fontsource/roboto để hiển thị tiếng Việt chuẩn)
 *
 * success/warning/error CỐ Ý không chạy theo thương hiệu — chúng mang ý nghĩa
 * trạng thái nghiệm thu, đổi màu theo từng khách sẽ làm sai thông điệp.
 */
export function createAppTheme(brandColor?: string | null): Theme {
  return createTheme({
  palette: {
    mode: 'light',
    primary: resolvePrimary(brandColor),
    secondary: {
      main: '#7c4dff',
    },
    success: {
      main: '#2e7d32',
    },
    warning: {
      main: '#ed6c02',
    },
    error: {
      main: '#d32f2f',
    },
    background: {
      default: '#f5f6f8',
      paper: '#ffffff',
    },
    text: {
      primary: '#1f2933',
      secondary: '#52606d',
    },
  },
  typography: {
    fontFamily: [
      'Roboto',
      '"Helvetica Neue"',
      'Arial',
      'sans-serif',
    ].join(','),
    h6: { fontWeight: 600 },
    button: { textTransform: 'none', fontWeight: 600 },
  },
  shape: {
    borderRadius: 8,
  },
  components: {
    MuiAppBar: {
      defaultProps: { elevation: 1 },
    },
    MuiButton: {
      defaultProps: { disableElevation: true },
    },
  },
  });
}

/**
 * Bảng màu trạng thái nghiệm thu (dùng cho StatusBadge).
 *  - PENDING: xám trung tính
 *  - CONFIRMED: xanh lá (success)
 *  - CHANGE_REQUESTED: cảnh báo/đỏ
 */
export type StatusChipColor = 'default' | 'success' | 'warning' | 'error' | 'info';

export interface StatusMeta {
  label: string;
  color: StatusChipColor;
}

/** Nhãn + màu tiếng Việt cho từng trạng thái nghiệm thu. */
export const ACCEPTANCE_STATUS_META: Record<AcceptanceStatus, StatusMeta> = {
  PENDING: { label: 'Chờ nghiệm thu', color: 'default' },
  CONFIRMED: { label: 'Đã xác nhận', color: 'success' },
  CHANGE_REQUESTED: { label: 'Yêu cầu thay đổi', color: 'warning' },
};

/** Màu nhấn cho luồng "Changed Request" (phía nhân viên xử lý). */
export const CHANGED_REQUEST_ACCENT = '#7c4dff';
